import { Control, type ControlOptions } from "../core/Control.js";
import type { MouseEventArgs } from "../core/events.js";
import { clamp, rectContains, type Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { ScrollBarPart, ScrollBarParts, Theme } from "../themes/Theme.js";

export type Orientation = "vertical" | "horizontal";

export interface ScrollBarOptions extends ControlOptions {
  orientation?: Orientation;
  min?: number;
  max?: number;
  /** Extent visible at once; the thumb is sized in proportion to it. */
  viewport?: number;
  value?: number;
  smallChange?: number;
  onScroll?: () => void;
}

const REPEAT_DELAY_MS = 400;
const REPEAT_INTERVAL_MS = 50;

/**
 * A scroll bar.
 *
 * The value range is `[min, max - viewport]`, so `value` is the position of
 * the visible window's leading edge rather than an abstract 0..100. Every
 * scrolling control in Webwin drives one of these.
 */
export class ScrollBar extends Control {
  #orientation: Orientation;
  #min: number;
  #max: number;
  #viewport: number;
  #value = 0;
  #hoveredPart: ScrollBarPart | null = null;
  #pressedPart: ScrollBarPart | null = null;
  #dragOffset = 0;
  #repeatTimer = 0;
  #repeatInterval = 0;

  smallChange: number;

  constructor(options: ScrollBarOptions = {}) {
    super({ width: 14, height: 100, ...options });
    this.#orientation = options.orientation ?? "vertical";
    this.#min = options.min ?? 0;
    this.#max = options.max ?? 100;
    this.#viewport = options.viewport ?? 10;
    this.smallChange = options.smallChange ?? 16;
    this.#value = clamp(options.value ?? 0, this.#min, this.maxValue);
    if (options.onScroll) this.on("scroll", options.onScroll);

    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("pointermove", (e) => this.#onPointerMove(e));
    this.on("pointerup", () => this.#endInteraction());
    this.on("pointerleave", () => {
      if (this.#hoveredPart) {
        this.#hoveredPart = null;
        this.invalidate();
      }
    });
  }

  get orientation(): Orientation {
    return this.#orientation;
  }
  set orientation(v: Orientation) {
    if (this.#orientation === v) return;
    this.#orientation = v;
    this.invalidate();
  }

  get vertical(): boolean {
    return this.#orientation === "vertical";
  }

  get min(): number {
    return this.#min;
  }
  get max(): number {
    return this.#max;
  }
  get viewport(): number {
    return this.#viewport;
  }

  /** Largest legal value: the content end minus one viewport. */
  get maxValue(): number {
    return Math.max(this.#min, this.#max - this.#viewport);
  }

  /** True when the content fits and the bar has nothing to scroll. */
  get isFull(): boolean {
    return this.#max - this.#min <= this.#viewport;
  }

  get largeChange(): number {
    return Math.max(this.smallChange, this.#viewport - this.smallChange);
  }

  get value(): number {
    return this.#value;
  }
  set value(v: number) {
    const next = clamp(v, this.#min, this.maxValue);
    if (next === this.#value) return;
    this.#value = next;
    this.emit("scroll");
    this.emit("change");
    this.invalidate();
  }

  /** Set the whole range in one go; keeps `value` in bounds. */
  setRange(min: number, max: number, viewport: number): void {
    const changed = min !== this.#min || max !== this.#max || viewport !== this.#viewport;
    this.#min = min;
    this.#max = Math.max(min, max);
    this.#viewport = Math.max(0, viewport);
    const clamped = clamp(this.#value, this.#min, this.maxValue);
    if (clamped !== this.#value) {
      this.#value = clamped;
      this.emit("scroll");
    }
    if (changed) this.invalidate();
  }

  scrollBy(delta: number): void {
    this.value = this.#value + delta;
  }

  // --- part geometry --------------------------------------------------------

  /** Rectangles for every piece of the bar, in local coordinates. */
  parts(theme: Theme): ScrollBarParts {
    const vertical = this.vertical;
    const bounds = this.localRect;
    const arrowSize = theme.metrics.scrollBarArrows
      ? Math.min(theme.metrics.scrollBarSize, vertical ? bounds.height / 2 : bounds.width / 2)
      : 0;

    let decrease: Rect | null = null;
    let increase: Rect | null = null;
    let track: Rect;
    if (vertical) {
      if (arrowSize > 0) {
        decrease = { x: 0, y: 0, width: bounds.width, height: arrowSize };
        increase = {
          x: 0,
          y: bounds.height - arrowSize,
          width: bounds.width,
          height: arrowSize,
        };
      }
      track = {
        x: 0,
        y: arrowSize,
        width: bounds.width,
        height: Math.max(0, bounds.height - arrowSize * 2),
      };
    } else {
      if (arrowSize > 0) {
        decrease = { x: 0, y: 0, width: arrowSize, height: bounds.height };
        increase = { x: bounds.width - arrowSize, y: 0, width: arrowSize, height: bounds.height };
      }
      track = {
        x: arrowSize,
        y: 0,
        width: Math.max(0, bounds.width - arrowSize * 2),
        height: bounds.height,
      };
    }

    const trackLength = vertical ? track.height : track.width;
    const range = this.#max - this.#min;
    let thumb: Rect = { x: 0, y: 0, width: 0, height: 0 };
    if (range > this.#viewport && trackLength > 0) {
      const ratio = clamp(this.#viewport / range, 0, 1);
      const thumbLength = clamp(
        Math.round(trackLength * ratio),
        Math.min(theme.metrics.scrollBarMinThumb, trackLength),
        trackLength,
      );
      const span = this.maxValue - this.#min;
      const t = span > 0 ? (this.#value - this.#min) / span : 0;
      const offset = Math.round(t * (trackLength - thumbLength));
      thumb = vertical
        ? { x: track.x, y: track.y + offset, width: track.width, height: thumbLength }
        : { x: track.x + offset, y: track.y, width: thumbLength, height: track.height };
    }

    return {
      bounds,
      track,
      thumb,
      decrease,
      increase,
      vertical,
      hoveredPart: this.#hoveredPart,
      pressedPart: this.#pressedPart,
    };
  }

  #partAt(p: { x: number; y: number }, theme: Theme): ScrollBarPart | null {
    const parts = this.parts(theme);
    if (parts.decrease && rectContains(parts.decrease, p)) return "decrease";
    if (parts.increase && rectContains(parts.increase, p)) return "increase";
    if (parts.thumb.width > 0 && rectContains(parts.thumb, p)) return "thumb";
    if (rectContains(parts.track, p)) {
      const before = this.vertical ? p.y < parts.thumb.y : p.x < parts.thumb.x;
      // With no thumb there is nothing to page against.
      if (parts.thumb.width <= 0 || parts.thumb.height <= 0) return null;
      return before ? "pageUp" : "pageDown";
    }
    return null;
  }

  // --- interaction ----------------------------------------------------------

  #onPointerDown(e: MouseEventArgs): void {
    const theme = this.desktop?.theme;
    if (!theme || e.button !== 0) return;
    const part = this.#partAt({ x: e.x, y: e.y }, theme);
    if (!part) return;
    e.handled = true;
    this.#pressedPart = part;
    this.desktop?.input.setCapture(this);

    if (part === "thumb") {
      const thumb = this.parts(theme).thumb;
      this.#dragOffset = this.vertical ? e.y - thumb.y : e.x - thumb.x;
    } else {
      this.#applyRepeatStep(part);
      this.#startRepeat(part, { x: e.x, y: e.y });
    }
    this.invalidate();
  }

  #onPointerMove(e: MouseEventArgs): void {
    const theme = this.desktop?.theme;
    if (!theme) return;

    if (this.#pressedPart === "thumb") {
      const parts = this.parts(theme);
      const trackLength = this.vertical ? parts.track.height : parts.track.width;
      const thumbLength = this.vertical ? parts.thumb.height : parts.thumb.width;
      const free = trackLength - thumbLength;
      if (free <= 0) return;
      const pos = (this.vertical ? e.y - parts.track.y : e.x - parts.track.x) - this.#dragOffset;
      const t = clamp(pos / free, 0, 1);
      this.value = this.#min + t * (this.maxValue - this.#min);
      return;
    }
    if (this.#pressedPart) return; // paging: the repeat timer drives it

    const part = this.#partAt({ x: e.x, y: e.y }, theme);
    if (part !== this.#hoveredPart) {
      this.#hoveredPart = part;
      this.invalidate();
    }
  }

  #applyRepeatStep(part: ScrollBarPart): void {
    switch (part) {
      case "decrease":
        this.scrollBy(-this.smallChange);
        break;
      case "increase":
        this.scrollBy(this.smallChange);
        break;
      case "pageUp":
        this.scrollBy(-this.largeChange);
        break;
      case "pageDown":
        this.scrollBy(this.largeChange);
        break;
      case "thumb":
        break;
    }
  }

  /** Auto-repeat while a stepper or track region is held down. */
  #startRepeat(part: ScrollBarPart, at: { x: number; y: number }): void {
    this.#stopRepeat();
    this.#repeatTimer = setTimeout(() => {
      this.#repeatInterval = setInterval(() => {
        const theme = this.desktop?.theme;
        // Stop paging once the thumb has caught up with the pointer.
        if (theme && (part === "pageUp" || part === "pageDown")) {
          if (this.#partAt(at, theme) !== part) {
            this.#stopRepeat();
            return;
          }
        }
        this.#applyRepeatStep(part);
      }, REPEAT_INTERVAL_MS);
    }, REPEAT_DELAY_MS);
  }

  #stopRepeat(): void {
    if (this.#repeatTimer !== 0) clearTimeout(this.#repeatTimer);
    if (this.#repeatInterval !== 0) clearInterval(this.#repeatInterval);
    this.#repeatTimer = 0;
    this.#repeatInterval = 0;
  }

  #endInteraction(): void {
    this.#stopRepeat();
    if (this.#pressedPart) {
      this.#pressedPart = null;
      this.desktop?.input.releaseCapture(this);
      this.invalidate();
    }
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    theme.drawScrollBar(rc, this.parts(theme), this.visualState());
  }

  /** All of it: a scroll bar is nothing but drag targets. */
  override handlesDragAt(): boolean {
    return true;
  }
}
